import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Layout, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useSidebarContext } from '@/contexts/sidebar-context';
import { useSettingsNavigation } from '@/hooks/use-settings-navigation';

import { NavbarRightSection } from './navbar-components';

const { Header } = Layout;

interface BaseNavbarProps {
  /**
   * Header style configuration
   */
  headerStyle?: 'fixed' | 'sticky';
}

export function BaseNavbar({ headerStyle = 'fixed' }: BaseNavbarProps) {
  const { t } = useTranslation();
  const sidebar = useSidebarContext();
  const [isDark, setIsDark] = useState(false);

  // Enable automatic context tracking for settings navigation
  useSettingsNavigation();

  useEffect(() => {
    const darkMode = document.documentElement.classList.contains('dark');
    setIsDark(darkMode);
  }, []);

  function handleToggleSidebar() {
    sidebar.desktop.toggle();
  }

  const baseStyle = {
    background: isDark ? '#2f165f' : '#5a2cab',
    height: '64px',
    lineHeight: '64px',
    padding: '0 24px',
  };

  const positionStyle =
    headerStyle === 'fixed'
      ? {
          position: 'fixed' as const,
          top: 0,
          zIndex: 30,
          width: '100%',
        }
      : {
          position: 'sticky' as const,
          top: 0,
          // Navbar must stay above any sticky content (e.g. Country Config
          // department panel headers sit at z-10 with `sticky top-16`).
          // Without this, those headers float over the navbar when scrolling.
          zIndex: 20,
          width: '100%',
        };

  return (
    <Header
      className="border-b border-gray-200 p-0 dark:border-gray-700"
      style={{
        ...baseStyle,
        ...positionStyle,
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Tooltip title={t('navigation.toggleSidebar')}>
            <button
              onClick={handleToggleSidebar}
              className="flex cursor-pointer items-center justify-center rounded p-2 text-gray-200 hover:bg-gray-700 hover:text-white"
            >
              {sidebar.desktop.isCollapsed ? (
                <MenuUnfoldOutlined className="text-xl" />
              ) : (
                <MenuFoldOutlined className="text-xl" />
              )}
            </button>
          </Tooltip>
          <Link to="/" className="flex items-center">
            <img
              alt="PermitsToFly logo"
              src="/images/logo.svg"
              style={{ width: 160, height: 40 }}
            />
          </Link>
        </div>

        <NavbarRightSection />
      </div>
    </Header>
  );
}
